import './StyleGuide.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { Form, Modal } from 'react-bootstrap';
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdSave } from 'react-icons/md';
import { StyleGuideRow } from '@blisspointmedia/bpm-types/dist/CopyTiger';
import { useFilteredList } from '@src/utils/search';
import { Button } from '../../Components/Button';
import { Appearance, Design, Intent, Size } from '../../Components/designConstants';
import { IconButton } from '../../Components/IconButton';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { FullPageSpinner } from '../../Components/Spinner';
import { useSetError } from '../../redux/modals';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { CopyTigerAlert } from './CopyTigerAlert';
import {
  addCopyTigerStyleGuide,
  deleteCopyTigerStyleGuide,
  DialogState,
  formatLastModified,
  getCopyTigerStyleGuides,
  getStyleGuideContent,
  SortBy,
  sortByKey
} from './copyTigerUtils';
import { SearchSortBar } from './SearchSortBar';

export const StyleGuide = () => {
  const [contentMap, setContentMap] = useState<Record<string, string>>({});
  const [editableContent, setEditableContent] = useState('');
  const [editableDescription, setEditableDescription] = useState('');
  const [editableTitle, setEditableTitle] = useState('');
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingIsEditMode, setPendingIsEditMode] = useState(false);
  const [pendingStyleGuideID, setPendingStyleGuideID] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStyleGuideID, setSelectedStyleGuideID] = useState('');
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>('a-to-z');
  const [styleGuides, setStyleGuides] = useState<StyleGuideRow[]>();
  const [dialog, setDialog] = useState<DialogState>();

  const companyInfo = useCompanyInfo();
  const company = companyInfo.cid;
  const setError = useSetError();

  const currentContent = useMemo(() => {
    if (selectedStyleGuideID && contentMap[selectedStyleGuideID]) {
      return contentMap[selectedStyleGuideID];
    }
    return '';
  }, [contentMap, selectedStyleGuideID]);

  const filteredGuides = useFilteredList(styleGuides ?? [], searchTerm, { keys: ['name'] });

  const sortedGuides = useMemo(() => sortByKey(filteredGuides, sortBy), [filteredGuides, sortBy]);

  const selectedStyleGuide = useMemo(
    () => (styleGuides ?? []).find(guide => guide.id === selectedStyleGuideID),
    [selectedStyleGuideID, styleGuides]
  );

  const hasUnsavedChanges = useMemo(() => {
    if (isCreatingNew) return true;
    if (!selectedStyleGuide || !isEditMode || isLoading) return false;
    return (
      editableTitle.trim() !== selectedStyleGuide.name ||
      editableDescription.trim() !== selectedStyleGuide.description ||
      editableContent.trim() !== currentContent
    );
  }, [
    currentContent,
    editableContent,
    editableDescription,
    editableTitle,
    isCreatingNew,
    isEditMode,
    isLoading,
    selectedStyleGuide
  ]);

  const enterEditMode = useCallback(
    (guide: StyleGuideRow, forceEditMode?: boolean) => {
      (async () => {
        if (selectedStyleGuideID === guide.id && isEditMode) return;
        if (hasUnsavedChanges && selectedStyleGuideID !== guide.id && !forceEditMode) {
          setPendingStyleGuideID(guide.id);
          setShowUnsavedChangesModal(true);
          return;
        }
        setSelectedStyleGuideID(guide.id);
        setEditableTitle(guide?.name ?? '');
        setEditableDescription(guide?.description ?? '');
        if (guide.id && contentMap[guide.id]) {
          setEditableContent(contentMap[guide.id]);
        } else if (guide.id) {
          try {
            const content = await getStyleGuideContent(guide.s3uri);
            setEditableContent(content);
            setContentMap(map => ({ ...map, [guide.id]: content }));
          } catch (e) {
            setError({
              message: `Failed to fetch style guide content. Error: ${e.message}`,
              reportError: e
            });
          }
        } else {
          setEditableContent('');
        }
        setIsEditMode(true);
      })();
    },
    [contentMap, hasUnsavedChanges, isEditMode, selectedStyleGuideID, setError]
  );

  const exitEditMode = useCallback(() => {
    if (pendingStyleGuideID) {
      setSelectedStyleGuideID(pendingStyleGuideID);
      if (pendingIsEditMode) {
        const pendingStyleGuide = (styleGuides ?? []).find(
          guide => guide.id === pendingStyleGuideID
        );
        enterEditMode(pendingStyleGuide as StyleGuideRow, true);
        setPendingIsEditMode(false);
      } else {
        setIsEditMode(false);
      }
    } else {
      setEditableContent('');
      setEditableDescription('');
      setEditableTitle('');
      setIsEditMode(false);
    }
    setIsCreatingNew(false);
    setPendingIsEditMode(false);
    setPendingStyleGuideID('');
    setShowUnsavedChangesModal(false);
  }, [enterEditMode, pendingIsEditMode, pendingStyleGuideID, setSelectedStyleGuideID, styleGuides]);

  const selectStyleGuide = useCallback(
    (guideID: string) => {
      if (isEditMode && hasUnsavedChanges) {
        setPendingStyleGuideID(guideID);
        setShowUnsavedChangesModal(true);
      } else {
        setSelectedStyleGuideID(guideID);
        if (isEditMode) {
          exitEditMode();
        }
      }
    },
    [exitEditMode, hasUnsavedChanges, isEditMode]
  );

  const confirmGuideSwitch = useCallback(exitEditMode, [exitEditMode]);

  const cancelGuideSwitch = useCallback(() => {
    setPendingStyleGuideID('');
    setPendingIsEditMode(false);
    setShowUnsavedChangesModal(false);
  }, []);

  const createNewStyleGuide = useCallback(() => {
    setIsCreatingNew(true);
    enterEditMode({ id: '', name: 'New Style Guide', description: '' } as StyleGuideRow);
    setPendingStyleGuideID('');
  }, [enterEditMode]);

  const saveStyleGuide = useCallback(
    () =>
      (async () => {
        const trimmedTitle = editableTitle.trim();
        const trimmedDescription = editableDescription.trim();
        const trimmedContent = editableContent.trim();
        if (!trimmedTitle) {
          setDialog({
            body: 'Style guide name cannot be empty.',
            title: 'Error',
            intent: Intent.ERROR
          });
          return;
        }
        if (!trimmedContent) {
          setDialog({
            body: 'Content cannot be empty.',
            title: 'Error',
            intent: Intent.ERROR
          });
          return;
        }
        if (!trimmedDescription.length) {
          setDialog({
            body: 'Description required!',
            title: 'Error',
            intent: Intent.ERROR
          });
          return;
        }
        if (trimmedDescription.length > 250) {
          setDialog({
            body: 'Description must be 250 characters or less.',
            title: 'Error',
            intent: Intent.ERROR
          });
          return;
        }
        if (
          selectedStyleGuide &&
          trimmedTitle === selectedStyleGuide.name &&
          trimmedDescription === selectedStyleGuide.description &&
          trimmedContent === currentContent
        ) {
          exitEditMode();
          setShowUnsavedChangesModal(false);
          return;
        }
        try {
          setIsLoading(true);
          const contentChanged = trimmedContent !== currentContent;
          let savedStyleGuide;
          if (contentChanged || selectedStyleGuide == null) {
            savedStyleGuide = await addCopyTigerStyleGuide(
              company,
              trimmedTitle,
              trimmedContent,
              trimmedDescription,
              selectedStyleGuide?.id
            );
          } else {
            savedStyleGuide = await addCopyTigerStyleGuide(
              company,
              trimmedTitle,
              '',
              trimmedDescription,
              selectedStyleGuide?.id
            );
          }
          setStyleGuides(await getCopyTigerStyleGuides(company));
          setContentMap(map => ({ ...map, [savedStyleGuide.id]: trimmedContent }));
          setSelectedStyleGuideID(savedStyleGuide ? savedStyleGuide.id : '');
          exitEditMode();
          setIsCreatingNew(false);
          setShowUnsavedChangesModal(false);
        } catch (e) {
          setError({
            message: `Failed to save style guide. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setIsLoading(false);
        }
      })(),
    [
      company,
      currentContent,
      editableContent,
      editableDescription,
      editableTitle,
      exitEditMode,
      selectedStyleGuide,
      setError
    ]
  );

  const handleDeleteStyleGuide = useCallback(
    (guide: StyleGuideRow) => {
      setDialog({
        title: 'Confirm Deletion',
        body: `Are you sure you want to delete "${guide.name}"? This action cannot be undone.`,
        intent: Intent.ERROR,
        onConfirm: async () => {
          setIsLoading(true);
          try {
            await deleteCopyTigerStyleGuide(company, guide.id);
            if (selectedStyleGuideID === guide.id) {
              setSelectedStyleGuideID('');
              if (isEditMode) {
                exitEditMode();
              }
            }
            setStyleGuides(await getCopyTigerStyleGuides(company));
            setDialog({
              title: 'Style Guide Deleted',
              body: `"${guide.name}" has been successfully deleted.`,
              intent: Intent.SUCCESS
            });
          } catch (e) {
            setError({
              message: `Failed to delete style guide. Error: ${e.message}`,
              reportError: e
            });
          } finally {
            setIsLoading(false);
          }
        }
      });
    },
    [company, exitEditMode, isEditMode, selectedStyleGuideID, setError]
  );

  const duplicateStyleGuide = useCallback(
    (guide: StyleGuideRow) => {
      (async () => {
        try {
          setIsLoading(true);
          const contentToSave = contentMap[guide.id]
            ? contentMap[guide.id]
            : await getStyleGuideContent(guide.s3uri);
          await addCopyTigerStyleGuide(
            company,
            `${guide.name} Copy`,
            contentToSave,
            guide.description
          );
          setStyleGuides(await getCopyTigerStyleGuides(company));
        } catch (e) {
          setError({
            message: `Failed to duplicate style guide. Error: ${e.message}`,
            reportError: e
          });
        } finally {
          setIsLoading(false);
        }
      })();
    },
    [company, contentMap, setError]
  );

  // Load Style Guides
  useEffect(() => {
    (async () => {
      if (company && styleGuides == null) {
        try {
          const guides = await getCopyTigerStyleGuides(company);
          setStyleGuides(guides);
          if (guides.length) {
            setSelectedStyleGuideID(guides[0].id);
          } else {
            setIsInitialLoadDone(true);
          }
        } catch (e) {
          setError({
            message: `Failed to fetch style guides. Error: ${e.message}`,
            reportError: e
          });
          setStyleGuides([]);
          setIsInitialLoadDone(true);
        }
      }
    })();
  }, [company, setError, styleGuides]);

  // Load Style Guide Content
  useEffect(() => {
    (async () => {
      if (selectedStyleGuide && contentMap[selectedStyleGuideID] == null && !isLoading) {
        try {
          setIsLoading(true);
          const content = await getStyleGuideContent(selectedStyleGuide.s3uri);
          setContentMap(map => ({ ...map, [selectedStyleGuideID]: content }));
        } catch (e) {
          setError({
            message: `Failed to fetch style guide content. Error: ${e.message}`,
            reportError: e
          });
          setContentMap(map => ({
            ...map,
            [selectedStyleGuideID]: `Failed to fetch style guide content. Error: ${e.message}`
          }));
        } finally {
          setIsLoading(false);
          setIsInitialLoadDone(true);
        }
      }
    })();
  }, [
    company,
    contentMap,
    isLoading,
    selectedStyleGuide,
    selectedStyleGuideID,
    setError,
    styleGuides
  ]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEditMode) {
        exitEditMode();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [exitEditMode, isEditMode]);

  return (
    <>
      {(!isInitialLoadDone || isLoading) && <FullPageSpinner transparent={isInitialLoadDone} />}
      <div className='copytiger-styleguide-container' data-testid='style-guide-page'>
        <div className='copytiger-styleguide-wrapper'>
          <div className='copytiger-styleguide-header'>
            <div className='copytiger-header-text'>
              <div className='copytiger-styleguide-title'>{companyInfo.name} Style Guides</div>
              <div className='copytiger-styleguide-subtitle'>
                A good style guide must clearly and accurately capture your brand&apos;s unique tone
                of voice and messaging, while also removing any redundant information to ensure the
                instructions are clear and the model does not ignore crucial elements.
              </div>
            </div>
            <div className='copytiger-styleguide-header-actions'>
              <Button
                disabled={isEditMode || isCreatingNew}
                onClick={createNewStyleGuide}
                leadingElement={<MdAdd />}
                size={Size.SM}
                data-testid='add-style-guide-button'
              >
                Add New Style Guide
              </Button>
            </div>
          </div>
          <div className='copytiger-styleguide-content'>
            <div className='copytiger-styleguide-sidebar'>
              <SearchSortBar
                searchId='style-guide-search'
                searchValue={searchTerm}
                onSearchChange={setSearchTerm}
                searchAriaLabel='Search style guides'
                sortValue={sortBy}
                onSortChange={setSortBy}
              />
              <div className='copytiger-sidebar-list' data-testid='style-guide-list'>
                {sortedGuides.map(guide => (
                  <InteractiveDiv
                    key={guide.id}
                    className={cn('copytiger-styleguide-item', {
                      'copytiger-selected': guide.id === selectedStyleGuideID
                    })}
                    onClick={
                      guide.id === selectedStyleGuideID
                        ? undefined
                        : () => selectStyleGuide(guide.id)
                    }
                  >
                    <div className='copytiger-item-header'>
                      <div className='copytiger-item-title'>{guide.name}</div>
                      <div className='copytiger-item-actions'>
                        <IconButton
                          design={Design.NEUTRAL}
                          appearance={Appearance.EMPTY}
                          onClick={() => {
                            enterEditMode(guide);
                            if (
                              isEditMode &&
                              guide.id !== selectedStyleGuideID &&
                              hasUnsavedChanges
                            ) {
                              setPendingIsEditMode(true);
                            }
                          }}
                          size={Size.SM}
                        >
                          <MdEdit />
                        </IconButton>
                        <IconButton
                          design={Design.NEUTRAL}
                          appearance={Appearance.EMPTY}
                          onClick={e => {
                            e.stopPropagation();
                            duplicateStyleGuide(guide);
                          }}
                          size={Size.SM}
                        >
                          <MdContentCopy />
                        </IconButton>
                        <IconButton
                          design={Design.NEUTRAL}
                          appearance={Appearance.EMPTY}
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteStyleGuide(guide);
                          }}
                          size={Size.SM}
                        >
                          <MdDelete />
                        </IconButton>
                      </div>
                    </div>
                    <div className='copytiger-item-description'>
                      {guide.description ?? 'No description available'}
                    </div>
                    <div className='copytiger-item-meta'>
                      Last modified {formatLastModified(guide.lastModified, guide.lastUser)}.
                    </div>
                  </InteractiveDiv>
                ))}
              </div>
            </div>
            <div className='copytiger-styleguide-main'>
              {selectedStyleGuide || isCreatingNew ? (
                <>
                  <div className='copytiger-main-header'>
                    {isEditMode ? (
                      <>
                        <div className='copytiger-title-edit-container'>
                          <Form.Control
                            type='text'
                            value={editableTitle}
                            onChange={e => setEditableTitle(e.target.value)}
                            className='copytiger-title-input'
                            placeholder='Style guide name...'
                          />
                        </div>
                        <div className='copytiger-main-meta'>
                          {selectedStyleGuide && (
                            <span className='copytiger-meta-info'>
                              Last Modified{' '}
                              {formatLastModified(
                                selectedStyleGuide.lastModified,
                                selectedStyleGuide.lastUser
                              )}
                            </span>
                          )}
                        </div>
                        <div className='copytiger-description-section'>
                          <div className='copytiger-description-edit-container'>
                            <textarea
                              value={editableDescription}
                              onChange={e => setEditableDescription(e.target.value)}
                              className='copytiger-description-textarea'
                              placeholder='Add a brief description of this style guide...'
                              maxLength={250}
                              rows={3}
                            />
                            <div className='copytiger-description-char-count'>
                              {editableDescription.length}/250 characters
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      selectedStyleGuide && (
                        <>
                          <div className='copytiger-main-title-display'>
                            <div className='copytiger-main-title'>{selectedStyleGuide.name}</div>
                            {selectedStyleGuideID && !isEditMode && (
                              <div className='copytiger-inline-icons'>
                                <MdDelete
                                  className='copytiger-inline-edit-icon'
                                  onClick={() => handleDeleteStyleGuide(selectedStyleGuide)}
                                  title='Delete Style Guide'
                                />
                                <MdEdit
                                  className='copytiger-inline-edit-icon'
                                  onClick={() => enterEditMode(selectedStyleGuide)}
                                  title='Edit Style Guide'
                                />
                              </div>
                            )}
                          </div>
                          <div className='copytiger-description-section'>
                            <div className='copytiger-description-display'>
                              <div className='copytiger-description-content'>
                                {selectedStyleGuide.description ? (
                                  <div className='copytiger-description-text'>
                                    {selectedStyleGuide.description}
                                  </div>
                                ) : (
                                  <div className='copytiger-description-placeholder'>
                                    No description
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className='copytiger-main-meta'>
                            <span className='copytiger-meta-info'>
                              Last Modified{' '}
                              {formatLastModified(
                                selectedStyleGuide.lastModified,
                                selectedStyleGuide.lastUser
                              )}
                            </span>
                          </div>
                        </>
                      )
                    )}
                  </div>
                  <div
                    className={cn('copytiger-main-content', {
                      'copytiger-editing-content': isEditMode
                    })}
                  >
                    <div className='copytiger-content-section'>
                      {isEditMode ? (
                        <div className='copytiger-content-edit-container'>
                          <div className='copytiger-content-edit-header' />
                          <textarea
                            value={editableContent}
                            onChange={e => setEditableContent(e.target.value)}
                            className='copytiger-content-textarea'
                            placeholder='Add your style guide content here...'
                          />
                        </div>
                      ) : (
                        <div className='copytiger-content-display-wrapper'>
                          <div className='copytiger-content-display-content'>
                            {currentContent ? (
                              <div className='copytiger-content-text'>{currentContent}</div>
                            ) : (
                              <div className='copytiger-content-placeholder'>
                                No content available
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className='copytiger-main-content'>
                  <div>
                    {styleGuides && styleGuides.length ? 'Select' : 'Create'} a style guide to view
                    its content
                  </div>
                </div>
              )}
              {isEditMode && (
                <div className='copytiger-main-footer'>
                  {selectedStyleGuideID && (
                    <div className='copytiger-footer-left'>
                      <Button
                        leadingElement={<MdDelete />}
                        onClick={() => handleDeleteStyleGuide(selectedStyleGuide!)}
                        size={Size.SM}
                        appearance={Appearance.EMPTY}
                      >
                        Delete Style Guide
                      </Button>
                    </div>
                  )}
                  <div className='copytiger-footer-right'>
                    <Button
                      leadingElement={<MdDelete />}
                      onClick={exitEditMode}
                      size={Size.SM}
                      appearance={Appearance.OUTLINED}
                      design={Design.NEUTRAL}
                    >
                      {hasUnsavedChanges ? 'Discard Changes' : 'Cancel'}
                    </Button>
                    <Button leadingElement={<MdSave />} onClick={saveStyleGuide} size={Size.SM}>
                      Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <CopyTigerAlert dialog={dialog} isLoading={isLoading} onHide={() => setDialog(undefined)} />
        <Modal
          show={!isLoading && showUnsavedChangesModal}
          onHide={cancelGuideSwitch}
          centered
          className='style-guide-unsaved-changes-modal'
        >
          <Modal.Header closeButton>
            <Modal.Title>Unsaved Changes</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              You have unsaved changes to the current style guide. Do you want to discard these
              changes and switch to another style guide?
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className='footer-left'>
              <Button appearance={Appearance.EMPTY} onClick={confirmGuideSwitch}>
                Discard Changes
              </Button>
            </div>
            <div className='footer-right'>
              <Button
                design={Design.NEUTRAL}
                onClick={cancelGuideSwitch}
                appearance={Appearance.OUTLINED}
              >
                Cancel
              </Button>
              <Button onClick={saveStyleGuide}>Save Changes</Button>
            </div>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};
